import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  computeAvailableSlots,
  SHORT_DAYS,
  DAY_LABELS,
  type BookingService,
  type BookingAvailability,
  type BookingBreak,
  type BookingHoliday,
  type BookingAppointment,
} from '@/lib/booking';

interface Props {
  serviceId: number;
  serviceName: string;
  onClose: () => void;
  /** Réserver le créneau choisi (date + heure mémorisées). */
  onBook?: (date: string, time: string) => void;
}

/** Date locale au format AAAA-MM-JJ (sans décalage de fuseau). */
function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function labelFor(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  const day = DAY_LABELS[(d.getDay() + 6) % 7];
  return `${day} ${d.getDate()} ${d.toLocaleDateString('fr-FR', { month: 'long' })}`;
}

/**
 * ServiceAvailabilityModal — « Voir les disponibilités » d'un service
 * réservable, directement depuis les cartes de l'assistant IA.
 * Lit le VRAI calendrier ZCalendar du prestataire (horaires, pauses, congés
 * et rendez-vous déjà pris) et affiche les prochains créneaux réellement libres.
 */
export default function ServiceAvailabilityModal({ serviceId, serviceName, onClose, onBook }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [service, setService] = useState<BookingService | null>(null);
  const [availabilities, setAvailabilities] = useState<BookingAvailability[]>([]);
  const [breaks, setBreaks] = useState<BookingBreak[]>([]);
  const [holidays, setHolidays] = useState<BookingHoliday[]>([]);
  const [appointments, setAppointments] = useState<BookingAppointment[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data: svc, error: svcError } = await supabase
          .from('booking_services')
          .select('id, owner, service_name, duration, location, description, statut')
          .eq('id', serviceId)
          .maybeSingle();
        if (cancelled) return;
        if (svcError || !svc) {
          setError("Ce service n'est pas réservable en ligne pour le moment.");
          return;
        }
        if (Number(svc.statut) !== 1) {
          setError("Ce prestataire n'a pas activé la réservation en ligne.");
          return;
        }
        setService(svc as BookingService);

        const [avRes, brRes, hoRes, apRes, avGlobal, brGlobal, hoGlobal] = await Promise.all([
          supabase.from('booking_availabilities').select('*').eq('service_id', serviceId),
          supabase.from('booking_breaks').select('*').eq('service_id', serviceId),
          supabase.from('booking_holidays').select('*').eq('service_id', serviceId),
          supabase.from('booking_appointments').select('id, date, time').eq('service_id', serviceId).neq('status', 'cancelled'),
          supabase.from('booking_availabilities').select('*').eq('service_id', 0),
          supabase.from('booking_breaks').select('*').eq('service_id', 0),
          supabase.from('booking_holidays').select('*').eq('service_id', 0),
        ]);
        if (cancelled) return;

        const avMap = new Map<string, BookingAvailability>();
        ((avGlobal.data || []) as BookingAvailability[]).forEach((a) => avMap.set(a.day_of_week, a));
        ((avRes.data || []) as BookingAvailability[]).forEach((a) => avMap.set(a.day_of_week, a));

        const brMap = new Map<string, BookingBreak>();
        ((brGlobal.data || []) as BookingBreak[]).forEach((b) => brMap.set(`${b.day_of_week}-${b.start_time}-${b.end_time}`, b));
        ((brRes.data || []) as BookingBreak[]).forEach((b) => brMap.set(`${b.day_of_week}-${b.start_time}-${b.end_time}`, b));

        const hoMap = new Map<string, BookingHoliday>();
        ((hoGlobal.data || []) as BookingHoliday[]).forEach((h) => hoMap.set(`${h.date_from}-${h.date_to}`, h));
        ((hoRes.data || []) as BookingHoliday[]).forEach((h) => hoMap.set(`${h.date_from}-${h.date_to}`, h));

        setAvailabilities(Array.from(avMap.values()));
        setBreaks(Array.from(brMap.values()));
        setHolidays(Array.from(hoMap.values()));
        setAppointments((apRes.data || []) as BookingAppointment[]);
      } catch {
        if (!cancelled) setError('Impossible de charger les disponibilités pour le moment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [serviceId]);

  /** Prochains jours avec au moins un créneau libre (6 jours maximum). */
  const days = useMemo(() => {
    if (!service || availabilities.length === 0) return [];
    const out: { date: string; slots: string[] }[] = [];
    const today = new Date();
    for (let i = 0; i < 30 && out.length < 6; i += 1) {
      const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
      const dateStr = localDateStr(d);
      if (holidays.some((h) => dateStr >= h.date_from && dateStr <= h.date_to)) continue;
      const slots = computeAvailableSlots(dateStr, service, availabilities, breaks, appointments);
      if (slots.length > 0) out.push({ date: dateStr, slots: slots.slice(0, 8) });
    }
    return out;
  }, [service, availabilities, breaks, holidays, appointments]);

  /** Jours d'ouverture (résumé lisible : « Lun, Mar, Mer… »). */
  const openDays = useMemo(() => {
    if (availabilities.length === 0) return '';
    const keys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const idx = keys.map((k, i) => (availabilities.some((a) => a.day_of_week === k) ? i : -1)).filter((i) => i >= 0);
    return idx.map((i) => SHORT_DAYS[i]).join(', ');
  }, [availabilities]);

  const handleSlot = useCallback((date: string, time: string) => {
    if (onBook) onBook(date, time);
    else onClose();
  }, [onBook, onClose]);

  return (
    <div className="fixed inset-0 z-[75] flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/45" onClick={onClose}></div>
      <div className="relative w-full sm:max-w-lg bg-background-50 rounded-t-2xl sm:rounded-xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 h-14 border-b border-background-200/70 flex-shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
              <i className="ri-calendar-2-line"></i>
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-foreground-950 truncate">Disponibilités</h3>
              <p className="text-[11px] text-foreground-500 truncate">{serviceName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {loading ? (
            <div className="py-14 flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
            </div>
          ) : error ? (
            <div className="py-10 flex flex-col items-center text-center">
              <i className="ri-calendar-close-line text-3xl text-foreground-300 mb-3"></i>
              <p className="text-sm text-foreground-600">{error}</p>
            </div>
          ) : days.length === 0 ? (
            <div className="py-10 flex flex-col items-center text-center">
              <i className="ri-calendar-close-line text-3xl text-foreground-300 mb-3"></i>
              <p className="text-sm font-semibold text-foreground-800 mb-1">Aucun créneau libre prochainement</p>
              <p className="text-xs text-foreground-500">
                Le calendrier de ce prestataire est complet sur les prochaines semaines.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-accent-800 bg-accent-100/80 px-2.5 py-1 rounded-full">
                  <i className="ri-time-line text-xs"></i>
                  {service?.duration || 30} min par rendez-vous
                </span>
                {openDays && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-secondary-900 bg-secondary-100/80 px-2.5 py-1 rounded-full">
                    <i className="ri-store-3-line text-xs"></i>
                    Ouvert : {openDays}
                  </span>
                )}
              </div>

              {days.map((day) => (
                <div key={day.date}>
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-calendar-event-line text-sm text-primary-600"></i>
                    <p className="text-xs font-bold text-foreground-900 capitalize">{labelFor(day.date)}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {day.slots.map((slot) => (
                      <button
                        key={`${day.date}-${slot}`}
                        type="button"
                        onClick={() => handleSlot(day.date, slot)}
                        className="px-3.5 py-2 rounded-full bg-background-50 border border-background-200/70 text-xs font-semibold text-foreground-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <p className="text-[11px] text-foreground-400 leading-relaxed pt-1 border-t border-background-200/60">
                Créneaux calculés en direct depuis le calendrier ZCalendar du prestataire
                (horaires, pauses, congés et rendez-vous déjà réservés).
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}